import React from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS } from "@FillInTableContainer/shared/constants/tableCreatorSubGroupShell.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useUpdateEntryData } from "@hooks/useUpdateArticleData";
import { useEntryDataState } from "@stores/entryData";
import { useInitEntryDataValue } from "@stores/initEntryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clickAddRightsScopeSubGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => void;
  handleClickToDeleteRightsScopeSubGroupShell: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  handleKeyDownToDeleteRightsScopeSubGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    subGroupShellIndex: number;
    subGroupShellKey: string | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDealLaScRightsScopeTableSubGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const initEntryData = useInitEntryDataValue(getPressIdFromURL());
  const updateEntryData = useUpdateEntryData();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();

  const handleAddSubGroupShell = (groupShellInfo: {
    tableIndexParameter: number;
    groupShellIndexParameter: number;
    groupShellKeyParameter: string | null;
    subGroupShellIndexParameter: number;
    subGroupShellKeyParameter: string | null;
  }) => {
    const { subGroupShellIndexParameter, subGroupShellKeyParameter } =
      groupShellInfo;

    if (subGroupShellKeyParameter === null) return;

    const targetShell =
      entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Rights Scope Table"];

    if (targetShell === undefined) return;

    const newArrayLength = targetShell.length + 1 || 1;
    const emptyArray = new Array(newArrayLength).fill({});

    const addSubGroupShellData = emptyArray.map((_, index) => {
      const milestoneSubGroupShellData =
        BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS[articleSection][
          subGroupShellKeyParameter
        ](initEntryData);

      if (index === subGroupShellIndexParameter) {
        return milestoneSubGroupShellData;
      }

      if (index > subGroupShellIndexParameter) {
        return targetShell[index - 1];
      }

      if (index < subGroupShellIndexParameter) {
        return targetShell[index];
      }

      return targetShell[index];
    });

    const changeSubGroupShellData = {
      ...entryData,
      [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
        ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
        "Rights Scope Table": addSubGroupShellData,
      },
    } as ArticleEntryData;

    setEntryData(changeSubGroupShellData);
    updateEntryData(changeSubGroupShellData);
    changeCurrentHistory({
      entryData: changeSubGroupShellData,
      changedDataInfo: {
        target: "subGroupShell",
        action: "add",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex,
          subGroupShellKey,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
  };

  const clickAddRightsScopeSubGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => {
    event.stopPropagation();
    handleAddSubGroupShell(groupShellInfo);
  };

  const handleClearSubGroupShell = () => {
    const clearRightsScopeSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Rights Scope Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Rights Scope Table"
          ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
            if (subGroupShellInfoIndex === info.subGroupShellIndex) {
              return {
                ...subGroupShellInfo,
                Asset: {
                  ...subGroupShellInfo.Asset,
                  value: [],
                },
                Exclusivity: {
                  ...subGroupShellInfo.Exclusivity,
                  value: [],
                },
                "Intended Use": {
                  ...subGroupShellInfo["Intended Use"],
                  value: [],
                },
                "Rights Flow": {
                  ...subGroupShellInfo["Rights Flow"],
                  "Axis Direction": {
                    ...subGroupShellInfo["Rights Flow"]["Axis Direction"],
                    value: [],
                  },
                  "Benefiting Axis": {
                    ...subGroupShellInfo["Rights Flow"]["Benefiting Axis"],
                    value: [],
                  },
                  "Contributing Axis": {
                    ...subGroupShellInfo["Rights Flow"]["Contributing Axis"],
                    value: [],
                  },
                },
                "Rights Limitation": {
                  ...subGroupShellInfo["Rights Limitation"],
                  value: [],
                },
                "Rights Scope": {
                  ...subGroupShellInfo["Rights Scope"],
                  value: [],
                },
                "Rights Scope Type": {
                  ...subGroupShellInfo["Rights Scope Type"],
                  value: [],
                },
                Territory: {
                  ...subGroupShellInfo.Territory,
                  value: [],
                },
                "Territory Exclude": {
                  ...subGroupShellInfo["Territory Exclude"],
                  value: [],
                },
                "Territory Include": {
                  ...subGroupShellInfo["Territory Include"],
                  value: [],
                },
              };
            }

            return subGroupShellInfo;
          }),
        },
      };
    };

    if (info.subGroupShellKey === "Rights Scope") {
      const rightsScopeLength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Rights Scope Table"]
          ?.length || 0;

      setEntryData(clearRightsScopeSubGroupShell());
      changeCurrentHistory({
        entryData: clearRightsScopeSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        rightsScopeLength > 1
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }
  };

  const handleDeleteSubGroupShell = () => {
    const deleteRightsScopeSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Rights Scope Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Rights Scope Table"
          ]?.filter((_, subGroupShellInfoIndex) => {
            return subGroupShellInfoIndex !== info.subGroupShellIndex;
          }),
        },
      };
    };

    if (info.subGroupShellKey === "Rights Scope") {
      setEntryData(deleteRightsScopeSubGroupShell());
      changeCurrentHistory({
        entryData: deleteRightsScopeSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const handleClickToDeleteRightsScopeSubGroupShell = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    if (focusStatus.type === "clear") {
      handleClearSubGroupShell();
    }
    if (focusStatus.type === "delete") {
      handleDeleteSubGroupShell();
    }
  };

  const handleKeyDownToDeleteRightsScopeSubGroupShell = (
    event: KeyboardEvent,
  ) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearSubGroupShell();
    }
    if (focusStatus.type === "delete" && event.key === "Delete") {
      handleDeleteSubGroupShell();
    }
  };

  return {
    clickAddRightsScopeSubGroupShellButton,
    handleClickToDeleteRightsScopeSubGroupShell,
    handleKeyDownToDeleteRightsScopeSubGroupShell,
  };
};
